//Dependencies............................................
import React, { useRef } from "react";
import axios from 'axios';
import {Button, Form, Input, Modal} from 'antd';
import Select from "react-select";

//Components..............................................
import {BASE_URL} from '../../../utils/constants';
import {FirmwareBase, JsonSchemaItem} from "../../../utils/interfaces";
import { useMutation, useQueryClient, UseQueryResult } from "@tanstack/react-query";
import { NotificationInstance } from "antd/es/notification/interface";
import { Control, Controller, FieldErrors, UseFormGetValues } from "react-hook-form";
import PatchNotesEditor from "../CustomWidgets/PatchNotesEditor";

interface Props {
    showAddModal: boolean,
    jsonSchemaListQuery: UseQueryResult<JsonSchemaItem[], Error>,
    toggleAddFirmwareModal: () => void,
    firmwareAddErrors: FieldErrors<FirmwareBase>,
    firmwareAddControl: Control<FirmwareBase, any, FirmwareBase>,
    firmwareAddGetValues: UseFormGetValues<FirmwareBase>,
    api: NotificationInstance,
    isValid: boolean
}



const AddFirmwareBaseListModal: React.FC<Props> = ({
    showAddModal,
    jsonSchemaListQuery,
    toggleAddFirmwareModal,
    firmwareAddErrors,
    firmwareAddControl,
    firmwareAddGetValues,
    api,
    isValid
}: Props) => {

    const queryClient = useQueryClient()

    const binaryAppRef = useRef<HTMLInputElement>(null);
    const binaryAppGblRef = useRef<HTMLInputElement>(null);
    const binaryLoaderRef = useRef<HTMLInputElement>(null);

    const addFirmwareBaseMutation = useMutation({
        mutationFn: (data: FirmwareBase) => {
            const formData = new FormData();
            formData.append('binary_app', data.binary_app[0])
            formData.append('binary_app_gbl', data.binary_app_gbl[0])
            formData.append('binary_loader', data.binary_loader[0])
            formData.append("version_name", data.version_name)
            formData.append("commit_hash", data.commit_hash)
            formData.append("changelog", data.changelog)
            formData.append("json_schema_base_id", data.json_schema_base_id.toString())
            return axios.post(`${BASE_URL}/generate_firmware`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            })
        },
        onSuccess: () => {
            handleCloseModal();
            toggleAddFirmwareModal();
            queryClient.invalidateQueries({ queryKey: ['baseFirmwareList'] })
            api.success({
                title: 'Firmware Base Added',
                description: `Firmware Base added successfully.`,
                placement: 'bottomRight',
                showProgress: true
            });
        },
        onError: (error: any) => {
            api.error({
                title: 'Firmware Base Addition Failed',
                description: `Failed to add Firmware Base ${error.message}`,
                placement: 'bottomRight',
                showProgress: true
            });
        }
    })

    const handleCloseModal = () => {
        if (binaryAppRef.current) binaryAppRef.current.value = "";
        if (binaryAppGblRef.current) binaryAppGblRef.current.value = "";
        if (binaryLoaderRef.current) binaryLoaderRef.current.value = "";

        toggleAddFirmwareModal();
    };

    return(
        <Modal
            open={showAddModal}
            onCancel={() => {handleCloseModal()}}
            title={"New Firmware"}
            width={760}
            closable={!addFirmwareBaseMutation.isPending}
            mask={{ closable: !addFirmwareBaseMutation.isPending }}
            footer={[
                <Button type="default" onClick={toggleAddFirmwareModal} disabled={addFirmwareBaseMutation.isPending}>Close</Button>,
                <Button
                    type="primary"
                    onClick={() => addFirmwareBaseMutation.mutate(firmwareAddGetValues())}
                    loading={addFirmwareBaseMutation.isPending}
                    disabled={!isValid || addFirmwareBaseMutation.isPending}
                >
                    Save Changes
                </Button>
            ]}
        >
            <Form layout="vertical" preserve={false}>
                <Form.Item
                    label="Firmware Version Name"
                    validateStatus={firmwareAddErrors.version_name ? "error" : ""}
                    help={firmwareAddErrors.version_name?.message}
                >
                    <Controller
                        name="version_name"
                        control={firmwareAddControl}
                        rules={{ required: "Firmware Version Name is required" }}
                        render={({ field }) =>(
                            <Input
                                {...field}
                                placeholder="Enter Firmware Version Name"
                            />
                        )}
                    />
                </Form.Item>
                <Form.Item
                    label="Commit Hash"
                    validateStatus={firmwareAddErrors.commit_hash ? "error" : ""}
                    help={firmwareAddErrors.commit_hash?.message}
                >
                    <Controller
                        name="commit_hash"
                        control={firmwareAddControl}
                        rules={{ required: "Commit Hash is required" }}
                        render={({ field }) =>(
                            <Input
                                {...field}
                                placeholder="Enter Commit Hash"
                            />
                        )}
                    />
                </Form.Item>
                <Form.Item
                    label="Changelog / Patch Notes"
                    validateStatus={firmwareAddErrors.changelog ? "error" : ""}
                    help={firmwareAddErrors.changelog?.message}
                >
                    <Controller
                        name="changelog"
                        control={firmwareAddControl}
                        rules={{ required: "Changelog is required" }}
                        render={({ field }) =>(
                            <PatchNotesEditor
                                value={field.value}
                                onChange={field.onChange}
                            />
                        )}
                    />
                </Form.Item>
                <Form.Item
                    label="Select the Schema Version"
                    validateStatus={firmwareAddErrors.json_schema_base_id ? "error" : ""}
                    help={firmwareAddErrors.json_schema_base_id?.message}
                >
                    <Controller
                        name="json_schema_base_id"
                        control={firmwareAddControl}
                        rules={{ required: "Schema Version is required" }}
                        render={({ field }) =>(
                            <Select
                                options={jsonSchemaListQuery.data}
                                getOptionLabel={(option) => option.version_schema_name}
                                getOptionValue={(option) => `${option.id}`}
                                value={field.value
                                    ? jsonSchemaListQuery.data?.find((state) => state.id === field.value)
                                    : null
                                }
                                onChange={(selectedOption) => field.onChange(selectedOption ? selectedOption.id : null)}
                            />
                        )}
                    />
                </Form.Item>
                <Form.Item
                    label="Firmware Loader"
                    validateStatus={firmwareAddErrors.binary_loader ? "error" : ""}
                    help={firmwareAddErrors.binary_loader?.message}
                >
                    <Controller
                        name="binary_loader"
                        control={firmwareAddControl}
                        rules={{ required: "Firmware Loader is required" }}
                        render={({ field }) =>(
                            <input
                                ref={binaryLoaderRef}
                                type="file"
                                accept=".s37"
                                onChange={(selectedOption) => field.onChange(selectedOption.target.files)}
                            />
                        )}
                    />
                </Form.Item>
                <Form.Item
                    label="Firmware App"
                    validateStatus={firmwareAddErrors.binary_app ? "error" : ""}
                    help={firmwareAddErrors.binary_app?.message}
                >
                    <Controller
                        name="binary_app"
                        control={firmwareAddControl}
                        rules={{ required: "Firmware App is required" }}
                        render={({ field }) =>(
                            <input
                                ref={binaryAppRef}
                                type="file"
                                accept=".s37"
                                onChange={(selectedOption) => field.onChange(selectedOption.target.files)}
                            />
                        )}
                    />
                </Form.Item>
                <Form.Item
                    label="Firmware App GBL"
                    validateStatus={firmwareAddErrors.binary_app_gbl ? "error" : ""}
                    help={firmwareAddErrors.binary_app_gbl?.message}
                >
                    <Controller
                        name="binary_app_gbl"
                        control={firmwareAddControl}
                        rules={{ required: "Firmware App GBL is required" }}
                        render={({ field }) =>(
                            <input
                                ref={binaryAppGblRef}
                                type="file"
                                accept=".gbl"
                                onChange={(selectedOption) => field.onChange(selectedOption.target.files)}
                            />
                        )}
                    />
                </Form.Item>
            </Form>
        </Modal>
    )
}

export default AddFirmwareBaseListModal;